import { useState, useCallback } from "react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../hooks/useAuth";
import { MeetingFormData } from "../types/meeting";
import { buildEventData, getApiEndpoint } from "../utils/meetingFormUtils";
import { parseEmailsFromCsv } from "../utils/meetingFormUtils";
import { getPlatformLabel } from "../constants/meetingConstants";

interface UseMeetingSubmissionReturn {
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitError: string | null;
  responseData: any;
  handleConfirmSubmit: (formData: MeetingFormData) => Promise<void>;
  resetSubmission: () => void;
}

export function useMeetingSubmission(): UseMeetingSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<any>(null);

  const { toast } = useToast();
  const { getAuthToken } = useAuth();

  const handleConfirmSubmit = useCallback(
    async (formData: MeetingFormData) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const token = getAuthToken();
        if (!token) {
          throw new Error("No authentication token found");
        }

        // Parse attendees
        const attendees = parseEmailsFromCsv(formData.attendees);

        // Build event data
        const eventData = buildEventData(formData, attendees);

        // Determine API endpoint
        const apiEndpoint = getApiEndpoint(formData.platform);

        // Make API call
        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(eventData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error ||
              `Failed to create ${getPlatformLabel(formData.platform)} event`
          );
        }

        const result = await response.json();
        setResponseData(result);
        setIsSubmitted(true);

        toast({
          title: "Meeting Created Successfully",
          description: `Your meeting has been created in ${getPlatformLabel(
            formData.platform
          )}.`,
        });
      } catch (error: any) {
        console.error("Error creating meeting:", error);
        setSubmitError(error.message);
        toast({
          title: "Failed to Create Meeting",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [getAuthToken, toast]
  );

  const resetSubmission = useCallback(() => {
    setIsSubmitted(false);
    setSubmitError(null);
    setResponseData(null);
  }, []);

  return {
    isSubmitting,
    isSubmitted,
    submitError,
    responseData,
    handleConfirmSubmit,
    resetSubmission,
  };
}
